import axios from 'axios'

import { Endpoint } from 'constants/API'
import API from 'utils/API/API'
import { logger } from 'utils/logging'

/** Backend expiry is 10 minutes; a 30s cadence leaves many missed beats before a live upload is declared dead */
export const HEARTBEAT_INTERVAL_MS = 30_000
/** Backend rejects larger bodies */
export const MAX_IDS_PER_CALL = 500
/** Must stay under HEARTBEAT_INTERVAL_MS: axios defaults to `timeout: 0`, so a TCP-level stall would otherwise pile runs up for the tab's lifetime */
export const LEASE_REQUEST_TIMEOUT_MS = 10_000

const tracked = new Set<string>()
let timer: ReturnType<typeof setInterval> | undefined
let inFlight = false
const warnedActions = new Set<string>()
const pendingReleases = new Set<string>()
let pendingFlush: Promise<void> | undefined

const chunk = <T>(items: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

/** A lease fault repeats on every beat, so report it once per session instead of every 30s forever */
const warnOnce = (action: string, message: string, metadata: Record<string, unknown>): void => {
  if (warnedActions.has(action)) return
  warnedActions.add(action)
  logger.warn(message, null, { context: { component: 'uploadLeases', action, metadata } })
}

const describeError = (error: unknown) => ({
  status: axios.isAxiosError(error) ? error.response?.status : undefined,
  message: error instanceof Error ? error.message : String(error),
})

const stopTimer = () => {
  if (timer !== undefined) {
    clearInterval(timer)
    timer = undefined
  }
}

const sendHeartbeat = async (): Promise<void> => {
  if (inFlight) return
  inFlight = true
  try {
    const ids = [...tracked]
    for (const batch of chunk(ids, MAX_IDS_PER_CALL)) {
      try {
        const { data } = await API.post<{ active: string[] }>(
          Endpoint.UPLOAD_LEASE_HEARTBEAT,
          { leaseIds: batch },
          { timeout: LEASE_REQUEST_TIMEOUT_MS },
          { endpoint: Endpoint.UPLOAD_LEASE_HEARTBEAT }
        )
        if (!Array.isArray(data?.active)) {
          // A 2xx that is not the expected shape (empty body, a proxy's HTML page, a renamed field)
          // tells us nothing about these leases. Treating it as "none are active" would drop the whole
          // batch and stop the timer, so keep them and keep beating, as the catch branch does.
          warnOnce('heartbeatResponse', 'Upload lease heartbeat returned an unusable body', { trackedCount: tracked.size })
          continue
        }
        const active = new Set(data.active)
        const lost = batch.filter((id) => tracked.has(id) && !active.has(id))
        if (lost.length > 0) {
          // The backend resolved these without us (expired while we were offline): their uploads will 412 at
          // finalize, so stop extending what is already gone and make the loss visible.
          lost.forEach((id) => tracked.delete(id))
          logger.warn('Upload leases expired while their uploads were still tracked', null, {
            context: {
              component: 'uploadLeases',
              action: 'heartbeat',
              metadata: { lostCount: lost.length, trackedCount: tracked.size, lostLeaseIds: lost.slice(0, 20) },
            },
          })
        }
      } catch (error) {
        // An expiring token makes every beat 401, and a 401 AxiosError payload is dropped before Sentry
        // on every path — so report a null payload with the status in metadata (as `_refreshRejectedSignedUrl` does).
        logger.warn('Upload lease heartbeat failed', null, {
          context: { component: 'uploadLeases', action: 'heartbeat', metadata: { count: batch.length, ...describeError(error) } },
        })
      }
    }
    if (tracked.size === 0) stopTimer()
  } finally {
    inFlight = false
  }
}

/** Keeps the placeholder behind a minted upload alive while the file is queued or uploading */
export const trackLease = (leaseId: string | undefined): void => {
  if (!leaseId) {
    // `leaseId` is optional so the frontend can ship ahead of the backend, which makes an endpoint that
    // stopped minting one indistinguishable from a healthy one until the placeholders are reaped.
    warnOnce('trackLease', 'Upload URL minted without a lease id', { trackedCount: tracked.size })
    return
  }
  tracked.add(leaseId)
  if (timer === undefined) {
    timer = setInterval(() => {
      void sendHeartbeat()
    }, HEARTBEAT_INTERVAL_MS)
  }
}

const flushReleases = async (): Promise<void> => {
  const ids = [...pendingReleases]
  pendingReleases.clear()
  for (const batch of chunk(ids, MAX_IDS_PER_CALL)) {
    try {
      await API.post(
        Endpoint.UPLOAD_LEASE_RELEASE,
        { leaseIds: batch },
        { timeout: LEASE_REQUEST_TIMEOUT_MS },
        { endpoint: Endpoint.UPLOAD_LEASE_RELEASE }
      )
    } catch (error) {
      logger.warn('Upload lease release failed', null, {
        context: {
          component: 'uploadLeases',
          action: 'release',
          metadata: { count: batch.length, leaseIds: batch.slice(0, 20), trackedCount: tracked.size, ...describeError(error) },
        },
      })
    }
  }
}

/**
 * Stops heartbeating and ends the lease: the backend deletes the placeholder unless the upload finished.
 *
 * The id leaves `tracked` synchronously so the next heartbeat snapshot already excludes it, but the POST
 * is deferred to a microtask: callers fire this unawaited from a `finally`, so one action that settles a
 * whole drop at once (a cancel, a network failure, navigating away) would otherwise issue one request —
 * each behind its own `getTokenSilently()` — per file. Everything released in the same tick coalesces
 * into `MAX_IDS_PER_CALL`-sized batches. The returned promise still settles once the POSTs are done.
 */
export const releaseLease = async (leaseId: string | undefined): Promise<void> => {
  if (!leaseId) return
  tracked.delete(leaseId)
  if (tracked.size === 0) stopTimer()
  pendingReleases.add(leaseId)
  pendingFlush ??= Promise.resolve().then(() => {
    pendingFlush = undefined
    return flushReleases()
  })
  return pendingFlush
}

/**
 * Drops every lease and stops the timer. Call this on logout: the heartbeat runs on the authenticated
 * API client, so a lease whose upload never settled would otherwise keep posting with a dead token
 * every 30s for the rest of the tab's lifetime — invisibly, since 401s are dropped on every path.
 */
export const resetUploadLeases = (): void => {
  tracked.clear()
  warnedActions.clear()
  pendingReleases.clear()
  pendingFlush = undefined
  inFlight = false
  stopTimer()
}
